// =============================================================================
// FILE: packages/renderer/src/components/branding/LogoMark.tsx
// PURPOSE: RCD² Engine brand mark — "Intelligence Node" identity.
//
//   Design language (Meta AI inspired):
//     • Stem   — full-height rounded pill (vertical stroke)
//     • Bowl   — smooth cubic-bezier D-arc, wide and prominent
//     • Leg    — quadratic-bezier diagonal, flows naturally from bowl
//     No sharp angles. No polygons. Pure curves throughout.
//
//   LogoMark          — tonal standalone icon (uses currentColor)
//   RCD2EngineLockup  — full horizontal lockup: mark + wordmark + descriptor
//   LogoLockup        — legacy compact lockup (kept for compatibility)
// =============================================================================

import type { JSX } from 'react';

// ─── Standalone icon ──────────────────────────────────────────────────────────

interface LogoMarkProps {
  size?: number;
  className?: string;
}

export function LogoMark({ size = 32, className = 'text-teal-400' }: LogoMarkProps): JSX.Element {
  return (
    <div
      data-testid="logo-mark"
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.22),
        background: 'linear-gradient(135deg, rgba(37,181,168,0.2) 0%, rgba(37,60,100,0.4) 60%, rgba(8,16,36,0.85) 100%)',
        border: '1px solid rgba(37,181,168,0.35)',
        boxShadow: '0 0 20px rgba(37,181,168,0.15), inset 0 1px 0 rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden',
        backdropFilter: 'blur(8px)',
        transform: 'perspective(200px) rotateY(0deg)',
        animation: 'lm-3d-idle 5s ease-in-out infinite',
      }}
    >
      {/* Shine sweep */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.07) 50%, transparent 65%)',
        animation: 'lm-shine 5s ease-in-out 2s infinite',
        pointerEvents: 'none',
      }} />
      <svg
        width={size * 0.68}
        height={size * 0.82}
        viewBox="0 0 80 96"
        role="img"
        aria-label="RCD² Intelligence Node"
        className={className}
        fill="none"
        overflow="visible"
      >
        <defs>
          <linearGradient id="lm-metal-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#7AE0D6" />
            <stop offset="50%"  stopColor="#25B5A8" />
            <stop offset="100%" stopColor="#0D7377" />
          </linearGradient>
          <filter id="lm-star2-glow" x="-300%" y="-300%" width="700%" height="700%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="8"  result="c1" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="4"  result="c2" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="c3" />
            <feMerge>
              <feMergeNode in="c1" /><feMergeNode in="c2" />
              <feMergeNode in="c3" /><feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <rect x="4" y="0" width="16" height="96" rx="8" fill="rgba(255,255,255,0.85)" />
        <path d="M 20,6 C 76,6 76,56 20,56" fill="none"
          stroke="url(#lm-metal-grad)" strokeWidth="14" strokeLinecap="round" />
        <path d="M 23,59 Q 46,63 78,94" fill="none"
          stroke="url(#lm-metal-grad)" strokeWidth="13" strokeLinecap="round" opacity="0.9" />
        <text x="50" y="22" fontFamily="'Inter',system-ui,sans-serif" fontWeight="900"
          fontSize="60" fill="#E0FFFE" filter="url(#lm-star2-glow)"
          style={{ animation: 'lm-glow-pulse 3.2s ease-in-out infinite' }}>2</text>
      </svg>
      <style>{`
        @keyframes lm-glow-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.80; }
        }
        @keyframes lm-3d-idle {
          0%, 100% { transform: perspective(200px) rotateY(-3deg) rotateX(1.5deg); }
          50%       { transform: perspective(200px) rotateY(3deg) rotateX(-1deg); }
        }
        @keyframes lm-shine {
          0%   { transform: translateX(-120%) skewX(-15deg); }
          100% { transform: translateX(320%) skewX(-15deg); }
        }
      `}</style>
    </div>
  );
}

// ─── Full horizontal lockup ────────────────────────────────────────────────────

interface LockupProps {
  /** Rendered height in pixels — width scales proportionally (viewBox 300×96). */
  height?: number;
}

/**
 * RCD² Engine full horizontal lockup.
 * Mark uses teal gradient on bowl + leg; stem adapts via CSS var.
 * Text: "RCD²" (primary) + "ENGINE" (descriptor, muted).
 *
 * Colors:
 *   Teal light  #7AE0D6  — bowl top
 *   Teal mid    #25B5A8  — bowl center
 *   Teal dark   #1B9480  — bowl + leg bottom
 *   Stem/leg    rgb(var(--fg))  — adapts: navy on light, white on dark
 */
export function RCD2EngineLockup({ height = 40 }: LockupProps): JSX.Element {
  const w = Math.round((300 / 96) * height);
  return (
    <svg
      viewBox="0 0 300 96"
      height={height}
      width={w}
      role="img"
      aria-label="RCD² Engine"
      data-testid="rcd2-engine-lockup"
      overflow="visible"
      fill="none"
    >
      <defs>
        <linearGradient id="rcd2-bowl-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#7AE0D6" />
          <stop offset="55%"  stopColor="#25B5A8" />
          <stop offset="100%" stopColor="#1B9480" />
        </linearGradient>
        {/* Premium glow for the ² mark — 4-layer corona, bleeds onto R */}
        <filter id="lku-star2-glow" x="-300%" y="-300%" width="700%" height="700%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="10"  result="corona-xl" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="5"   result="corona"    />
          <feGaussianBlur in="SourceGraphic" stdDeviation="2"   result="halo"      />
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.8" result="inner"     />
          <feMerge>
            <feMergeNode in="corona-xl" />
            <feMergeNode in="corona-xl" />
            <feMergeNode in="corona"    />
            <feMergeNode in="corona"    />
            <feMergeNode in="halo"      />
            <feMergeNode in="inner"     />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ── Mark (x 0–90, y 0–96) ──────────────────────────────────────── */}

      {/* Stem — full-height rounded pill */}
      <rect
        x="4" y="0" width="16" height="96" rx="8"
        style={{ fill: 'rgb(var(--fg))' }}
      />

      {/* Bowl — smooth cubic-bezier sweep with teal gradient */}
      <path
        d="M 20,6 C 76,6 76,56 20,56"
        fill="none"
        stroke="url(#rcd2-bowl-grad)"
        strokeWidth="14"
        strokeLinecap="round"
      />

      {/* Leg — flowing quadratic-bezier diagonal, teal gradient */}
      <path
        d="M 23,59 Q 46,63 78,94"
        fill="none"
        stroke="url(#rcd2-bowl-grad)"
        strokeWidth="12"
        strokeLinecap="round"
        opacity="0.88"
      />

      {/* Superscript ² — premium powered-on indicator, 50% larger encore, white/cyan star-burst */}
      <text
        x="50"
        y="20"
        fontFamily="'Inter', 'Segoe UI', system-ui, sans-serif"
        fontWeight="900"
        fontSize="63"
        fill="#E0FFFE"
        filter="url(#lku-star2-glow)"
        style={{ animation: 'lku-glow-pulse 3.2s ease-in-out infinite' }}
      >2</text>
      <style>{`
        @keyframes lku-glow-pulse {
          0%, 100% { opacity: 1; filter: url(#lku-star2-glow) brightness(1); }
          50%       { opacity: 0.80; filter: url(#lku-star2-glow) brightness(1.4); }
        }
      `}</style>

      {/* Node accent — small orb at bowl apex */}
      <circle cx="74" cy="31" r="5" fill="#7AE0D6" opacity="0.85" />

      {/* ── Wordmark (x 108–300) ───────────────────────────────────────── */}

      {/* RCD² */}
      <text
        x="108"
        y="53"
        fontFamily="'Inter', 'Segoe UI', system-ui, sans-serif"
        fontWeight="800"
        fontSize="38"
        style={{ fill: 'rgb(var(--fg))', letterSpacing: '-0.5px' }}
      >
        RCD
        <tspan dy="-14" fontSize="20" fontWeight="700">2</tspan>
      </text>

      {/* ENGINE descriptor */}
      <text
        x="109"
        y="85"
        fontFamily="'Inter', 'Segoe UI', system-ui, sans-serif"
        fontWeight="700"
        fontSize="29"
        style={{ fill: 'rgb(var(--muted))', letterSpacing: '3px' }}
      >
        ENGINE
      </text>
    </svg>
  );
}

// ─── Legacy compact lockup ─────────────────────────────────────────────────────

interface LogoLockupProps {
  size?: number;
  tagline?: string;
}

export function LogoLockup({ size = 24, tagline }: LogoLockupProps): JSX.Element {
  const markSize = Math.round(size * 1.6);
  return (
    <div className="flex items-center gap-3" data-testid="logo-lockup">
      <LogoMark size={markSize} className="text-teal-400" />
      <div className="flex flex-col leading-tight">
        <span
          className="font-extrabold tracking-[0.12em]"
          style={{ fontSize: size, color: 'rgb(var(--fg))' }}
        >
          RCD²
        </span>
        {tagline !== undefined && (
          <span
            className="text-[10px] uppercase tracking-[0.22em] mt-0.5"
            style={{ color: 'rgb(var(--muted)/0.6)' }}
          >
            {tagline}
          </span>
        )}
      </div>
    </div>
  );
}
